import { useTranslation } from 'react-i18next';

import type { CategoryShare } from '@/services/types';

/** Labels across a share list. More than the denominator means multi-label. */
function labelCount(shares: CategoryShare[]): number {
  return shares.reduce((sum, share) => sum + share.count, 0);
}

/**
 * A list of category shares under a title carrying the denominator.
 *
 * Shared by the bidding tracks and the appeal record. The share is rendered as
 * the API computed it, over the denominator the caller names; nothing here
 * re-divides. `render` lets a caller translate a CODE (an outcome code has a
 * locale entry; a TIPE penalty type is shown raw), falling back to the code
 * itself so an unknown value is visible rather than blank.
 *
 * `multiLabelNote` is called ONLY when the labels outnumber the denominator,
 * with both figures -- so the caveat is derived from the numbers the panel
 * prints and disappears by itself if the source ever becomes single-label.
 */
export function SharePanel({
  title,
  shares,
  total,
  multiLabelNote,
  render,
}: {
  title: string;
  shares: CategoryShare[];
  total: number;
  multiLabelNote?: (labels: number, total: number) => string;
  render?: (code: string) => string;
}) {
  const { i18n } = useTranslation();
  const labels = labelCount(shares);
  const note = multiLabelNote && labels > total ? multiLabelNote(labels, total) : null;
  const number = new Intl.NumberFormat(i18n.language);
  const percent = new Intl.NumberFormat(i18n.language, {
    style: 'percent',
    maximumFractionDigits: 1,
  });

  return (
    <div className="rounded-md border bg-background p-3">
      <h3 className="mb-2 text-xs font-medium text-foreground">
        {title} <span className="font-normal text-muted-foreground">({number.format(total)})</span>
      </h3>
      <ul className="space-y-1">
        {shares.map((share) => (
          <li
            key={share.code}
            data-share-code={share.code}
            className="flex items-baseline justify-between gap-3 border-b pb-1 text-xs last:border-b-0"
          >
            <span className="text-foreground">{render ? render(share.code) : share.code}</span>
            <span className="tabular-nums text-muted-foreground">
              {number.format(share.count)} · {percent.format(share.share)}
            </span>
          </li>
        ))}
      </ul>
      {note ? <p className="mt-2 text-xs text-muted-foreground">{note}</p> : null}
    </div>
  );
}
